import type { FeedMessage } from "@/types";

export interface FeedPage {
  messages: FeedMessage[];
  has_more: boolean;
  has_more_newer?: boolean;
  count: number;
}

function feedBase(): string {
  if (import.meta.env.DEV) return "";
  return (import.meta.env.VITE_WIDGET_API_URL || "").replace(/\/+$/, "");
}

export type FeedCursor = { beforeId: string; beforeDate: string };

export type FeedAfterCursor = { afterId: string; afterDate: string };

export async function fetchFeedPage(
  limit = 100,
  cursor?: FeedCursor | FeedAfterCursor
): Promise<FeedPage> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor && "beforeId" in cursor) {
    if (cursor.beforeId) params.set("before_id", cursor.beforeId);
    if (cursor.beforeDate) params.set("before_date", cursor.beforeDate);
  }
  if (cursor && "afterId" in cursor) {
    if (cursor.afterId) params.set("after_id", cursor.afterId);
    if (cursor.afterDate) params.set("after_date", cursor.afterDate);
  }
  const url = `${feedBase()}/api/feed?${params}`;
  const res = await fetch(url);
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (typeof body?.detail === "string") detail = body.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return (await res.json()) as FeedPage;
}

export async function fetchFeedAround(
  messageId: string,
  before = 10,
  after = 10
): Promise<FeedPage> {
  const params = new URLSearchParams({
    before: String(before),
    after: String(after),
  });
  const url = `${feedBase()}/api/feed/around/${encodeURIComponent(messageId)}?${params}`;
  const res = await fetch(url);
  if (res.status === 404) {
    throw new Error(`message ${messageId} not found`);
  }
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (typeof body?.detail === "string") detail = body.detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return (await res.json()) as FeedPage;
}

export async function fetchMessageById(id: string): Promise<FeedMessage | null> {
  const res = await fetch(`${feedBase()}/api/feed/message/${id}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as FeedMessage;
}
